import { useContext, useEffect, useMemo, useState } from "react";
import { Alert, AlertTitle, Button, Divider, Drawer } from "@mui/material";
import {
	CancelOrderText,
	CarrierText,
	EmptyLinkedOrdersHelperText,
	ErrorLinkedOrdersHelperText,
	EstimatedDeliveryText,
	EstimatedDeliveryToolTipDesc,
	exportAcknowledgementText,
	exportOrderDetailsText,
	GeneratingPDFText,
	LinkedOrdersErrorHeaderText,
	LowesOrderAddOns,
	myOrdersLinkText,
	NoLinkedOrdersHeaderText,
	OrderAddOns,
	OrderDateText,
	ConfirmationNumberText,
	OrderNumberText,
	OrderReplacementsText,
	OrdersText,
	PDFDownloadText,
	PDFErrorText,
	PONumberText,
	ReplacementOrderSubmitted,
	ReplacementOrderSubmittedText,
	ReturnAuthorizationFormText,
	StatusUpdatedText,
	TrackingNumberText,
	TruckNumberText,
	UnavailableDataPlaceholderText,
	ViewAssociatedOrdersText
} from "constants/text";
import { salesforceBaseURL } from "constants/endpoints";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import InsertLink from "@mui/icons-material/InsertLink";
import { ORDER_STATUS_REASONS } from "constants/orderStatusHeaderStatusReasons";
import { dateFormat } from "utils/date";
import { checkForAndReplaceNullEmptyWhitespace, convertToTitleCase } from "utils/string";
import { useNavigate } from "react-router";
import { filterSpecificBrands, getOrderTypeDisplayLabel } from "utils/order";
import ExportButtons from "../ExportFile/ExportButtons";
import useOrderDetailsPDFGenerator from "hooks/useOrderDetailsPDFGenerator";
import {
	AcknowledgementPriceFormat,
	OrderActionType,
	OrderStatus,
	OrderType,
	UserChannel
} from "types/declarations/enums.ts";
import { generateMyOrderDetailsExcel } from "./helpers/MyOrderDetailsExportsHelper";
import { useGetUserDetailsQuery } from "features/api/userApi.ts";
import { useSelector } from "react-redux";
import { RootState } from "stores/application.store";
import {
	ButtonContainer,
	CarrierInfoContainer,
	DeliveryContent,
	DrawerContainer,
	EstimatedDelivery,
	EstimatedDeliveryToolTip,
	NumberOfOrders,
	OrderDateColumn,
	OrderDetailsGrayBox,
	OrderDetailsHeaderContent,
	OrderDetailsHeaderContentDetails,
	OrderDetailsHeaderJobName,
	OrderDetailsLinksContainer,
	OrderDetailsMyOrdersLink,
	OrderETA,
	OrderNumberPoNumberDivider,
	OrderNumberPoNumberText,
	OrderNumberPoNumberWrapper,
	PillContainer,
	ReplacementOrderSubmittedBanner,
	RequestedDateContainer,
	StatusContainer,
	StatusReason,
	StatusWrapper,
	StyledChip,
	TrackingNumberWrapper
} from "./OrderDetailsHeader.styles";
import { useGetLinkedOrdersQuery } from "features/api/orderApi.ts";
import FlyoutHeader from "./AssociatedOrders/FlyoutHeader";
import IndividualLinkedOrderCard from "./AssociatedOrders/IndividualLinkedOrderCard";
import EmptyErrorStates from "./AssociatedOrders/EmptyErrorStates";
import { ShoppingCartFlyoutScrollableWrapper } from "../Replacements/ShoppingCartFlyout.styles";
import Link from "components/Common/Link/Link";
import Tooltip from "@mui/material/Tooltip";
import useSnackbarEffect from "hooks/useSnackbarEffect";
import { useGetAccountsByIdQuery, useGetBillToInfoQuery } from "features/api/accountApi";
import { OrderSummaryCardChip } from "components/OrderSummaryCard/OrderSummaryCard.styles.ts";
import {
	CancellationNotProcessedBannerDetails,
	CancellationNotProcessedBannerTitle,
	CancellationSubmittedBannerDetails,
	CancellationSubmittedBannerTitle,
	NewOrderRequestedDeliveryDate
} from "../NewOrders/constants.ts";
import { models } from "types/api/viewModels.ts";
import CancelIcon from "@mui/icons-material/Cancel";
import ConfigurationsContext from "../../context/configurations/ConfigurationsContext.ts";
import Snackbar from "components/Common/SnackBar/SnackBar.tsx";
import { useDialog } from "hooks/useDialog.ts";
import { DialogTracking } from "components/Dialog/components/DialogTracking.ts";
import { useGetPermissionsQuery } from "features/api/permissionsApi.ts";
import SupportCTA from "components/Common/SupportCTA/SupportCTA";
import { getSupportFormUrl } from "utils/supportForm";

enum CancellationStatus {
	CancellationRequested = "CancellationRequested",
	CancellationDenied = "CancellationDenied"
}

const OrderDetailsHeader = ({ orderDetail, groupedOrders }: Props) => {
	const { data: permissions } = useGetPermissionsQuery();
	const formattedOrderType = getOrderTypeDisplayLabel(orderDetail.orderType);
	const isMerchOrder = orderDetail.orderType === OrderType.MERCH;
	const [statusReasonMessage, setStatusReasonMessage] = useState("");
	const [defaultColor, setDefaultColor] = useState("");
	const [isOpen, setIsOpen] = useState<boolean>(false);
	const navigateTo = useNavigate();
	const { data: linkedOrders, error } = useGetLinkedOrdersQuery(orderDetail?.orderId ?? "");
	const linkedOrdersCount = linkedOrders?.length ? linkedOrders.length : 0;
	const { configurationsData } = useContext(ConfigurationsContext);
	const { openDialog } = useDialog();

	const replacementOrders: string[] = useSelector((state: RootState) => state.submittedReplacementOrders.orders);
	const hasReplacementOrder = useMemo(() => {
		return replacementOrders.find((originalOrderId) => originalOrderId === orderDetail.orderId);
	}, [replacementOrders, orderDetail.orderId]);

	const handleAssociatedOrders = () => {
		setIsOpen((prevState) => !prevState);
	};

	const handleOnClose = () => {
		setIsOpen((prevState) => !prevState);
	};

	const showEstimatedDeliveryText = (orderStatus?: string) => {
		return (
			orderStatus !== OrderStatus.DELIVERED &&
			orderStatus !== OrderStatus.CANCELLED &&
			orderStatus !== OrderStatus.INVOICED
		);
	};

	const orderDetailsHeaderStatusUpdate = orderDetail.updatedAt;

	useEffect(() => {
		ORDER_STATUS_REASONS.forEach(
			(reason) => reason.status === orderDetail.status && setStatusReasonMessage(reason.description)
		);

		ORDER_STATUS_REASONS.forEach(
			(reason) => reason.status === orderDetail.status && setDefaultColor(reason.statusColor)
		);
	});

	const handleOrderReplacements = () => {
		navigateTo(`/replacements/${orderDetail.orderId}`);
	};

	const { data: userDetails } = useGetUserDetailsQuery();
	const { data: accounts } = useGetAccountsByIdQuery(orderDetail.orderId ?? "");
	const accountId = accounts?.[0]?.accountId;
	const billToInfoQueryArgs = useMemo(
		() => ({ accountId: accountId ?? "", billToId: orderDetail.billToId ?? "" }),
		[accountId, orderDetail.billToId]
	);
	const { data: billToInfo } = useGetBillToInfoQuery(billToInfoQueryArgs, {
		skip: !accountId || !orderDetail.billToId
	});
	const isLowesUser = userDetails?.userChannel === UserChannel.LOWES;
	const effectiveAckPermission = isMerchOrder
		? AcknowledgementPriceFormat.LIST
		: permissions?.acknowledgementPriceFormat;
	const supportFormUrl = getSupportFormUrl({
		type: "Order Help",
		orderNumber: orderDetail.orderNumber,
		brand: orderDetail.configurations?.[0]?.globals?.brand?.description,
		poNumber: orderDetail.poNumber
	});
	const isReturnAuthEnabled = configurationsData?.featureFlags?.returnAuthEnabled === true;
	const isAcknowledgements =
		orderDetail.isAcknowledgementEligible && configurationsData?.featureFlags?.acknowledgementsEnabled;

	const { pdfData, generateOrderDetailsPDF } = useOrderDetailsPDFGenerator(
		orderDetail,
		groupedOrders,
		billToInfo,
		configurationsData
	);
	const { snackbarOpen } = useSnackbarEffect(pdfData.loading);

	const goToOrderDetails = (orderId: string | number) => {
		setIsOpen(false);
		navigateTo(`/details/${orderId}`);
	};

	const configAccountNumbers = orderDetail?.configurations?.map((config) => config.accountNumber);
	const orderUserAccountDetail = accounts?.find((account) => configAccountNumbers?.includes(account.accountNumber));
	const isReplacementEligible =
		orderDetail.isReplacementEligible && (orderUserAccountDetail?.isActive || userDetails?.role === "CARE");
	const isHomeDepotOrLowesUser =
		userDetails?.userChannel === UserChannel.HOME_DEPOT || userDetails?.userChannel === UserChannel.LOWES;
	const poNumber = orderDetail.poNumber?.trim();
	const orderNumber = orderDetail.orderNumber?.trim();
	const supportFormBrand =
		orderDetail.configurations?.[0]?.globals?.brand?.description?.trim() ||
		orderDetail.configurations?.[0]?.globals?.brand?.code?.trim();
	const brandName = supportFormBrand ? filterSpecificBrands(supportFormBrand.replaceAll("_", " ")) : "";
	const hasReturnFormOrderContext = Boolean(orderNumber);
	const isReturnAuthorizationEligible = Boolean(
		isReplacementEligible &&
			isHomeDepotOrLowesUser &&
			isReturnAuthEnabled &&
			orderDetail.isReturnEligible === true &&
			hasReturnFormOrderContext
	);
	const returnAuthorizationFormUrl = useMemo(() => {
		if (!orderNumber) return "";

		const supportPath = "/s/support";
		const normalizedSalesforceBase = salesforceBaseURL.endsWith("/")
			? salesforceBaseURL.slice(0, -1)
			: salesforceBaseURL;
		const supportQueryParams = new URLSearchParams({
			type: "Return Authorization",
			order_num: orderNumber
		});
		if (poNumber) {
			supportQueryParams.set("po_num", poNumber);
		}
		if (brandName) {
			supportQueryParams.set("brand", brandName);
		}

		return `${normalizedSalesforceBase}${supportPath}?${supportQueryParams.toString().replace(/\+/g, "%20")}`;
	}, [poNumber, orderNumber, brandName]);

	const carrierInfo = [
		{
			label: CarrierText,
			info: orderDetail.shippingDetails?.carrierName,
			dataTestId: "orderDetailsHeader-carrierName"
		},
		{
			label: TruckNumberText,
			info: orderDetail.shippingDetails?.truckNumber,
			dataTestId: "orderDetailsHeader-truckNumber"
		},
		{
			label: TrackingNumberText,
			info: {
				trackingNumber: orderDetail.shippingDetails?.trackingNumber,
				trackingUrls: orderDetail.shippingDetails?.trackingUrls
			},
			dataTestId: "orderDetailsHeader-trackingNumber"
		}
	];

	const goToAddOnOrder = () => {
		navigateTo(`/add-ons/${orderDetail.orderId}`);
	};

	const renderTrackingUrls = (info: (typeof carrierInfo)[number]["info"]) => {
		const isTrackingObj = typeof info !== "string";

		return isTrackingObj ? (
			info?.trackingNumber?.split(",").map((eachTrackingNumber) => {
				const trackingUrl = info?.trackingUrls?.find((url) => url.includes(eachTrackingNumber));

				return trackingUrl ? (
					<TrackingNumberWrapper
						key={trackingUrl}
						href={trackingUrl}
						target="_blank"
						rel="noreferrer"
					>
						{eachTrackingNumber}
					</TrackingNumberWrapper>
				) : (
					<div key={eachTrackingNumber}>{eachTrackingNumber}</div>
				);
			})
		) : (
			<div>{info}</div>
		);
	};

	const handleCancelOrder = () => {
		openDialog({ dialogStatus: DialogTracking.CANCEL_ORDER, dialogId: orderDetail.orderId });
	};

	return (
		<div>
			<OrderDetailsMyOrdersLink>
				<Link
					to="/"
					hasBackIcon
					id="order-details-my-orders-link"
					data-id="order-details-my-orders-link"
					data-testid="orderDetailsHeader-myOrdersLink"
				>
					{myOrdersLinkText}
				</Link>
				<Snackbar
					open={snackbarOpen}
					isLoading={pdfData.loading}
					isError={Boolean(pdfData.error)}
					loadingText={GeneratingPDFText}
					successText={PDFDownloadText}
					errorText={PDFErrorText}
				/>
			</OrderDetailsMyOrdersLink>

			<OrderDateColumn data-testid="orderDetails-orderDateColumn">
				<div
					className="overline"
					data-testid="order-details-orderDate"
				>
					{OrderDateText.toUpperCase()}:{" "}
					{dateFormat(orderDetail?.orderDate ?? "") ?? UnavailableDataPlaceholderText}
				</div>
			</OrderDateColumn>
			<OrderDetailsHeaderContent>
				<OrderDetailsHeaderContentDetails>
					<OrderDetailsHeaderJobName>
						<h4
							data-testid="order-details-header-job-name"
							lang="en"
						>
							{orderDetail.jobName ? orderDetail.jobName : orderDetail.poNumber}
						</h4>

						<StyledChip
							label={formattedOrderType}
							data-testid="orderDetailsHeader-orderTypeChip"
						/>
					</OrderDetailsHeaderJobName>

					<OrderDetailsLinksContainer>
						<Button
							variant="text"
							onClick={handleAssociatedOrders}
							data-testid="associated-orders-link"
							aria-label="associated-orders-link"
							startIcon={
								<InsertLink
									aria-label="insert-link-icon"
									data-testid="insert-link-icon"
								/>
							}
							sx={{
								fontFamily: "canada-type-gibson, sans-serif",
								fontWeight: "var(--font-weight-medium)",
								fontSize: 13
							}}
						>
							{ViewAssociatedOrdersText.toUpperCase()}
						</Button>
						{isReturnAuthorizationEligible && (
							<Button
								component="a"
								variant="text"
								href={returnAuthorizationFormUrl}
								target="_blank"
								rel="noreferrer"
								data-testid="return-authorization-form-link"
								data-id="return-authorization-form-link"
								aria-label="return authorization form link"
								startIcon={
									<img
										src="/assets/assignment_return.svg"
										alt=""
										aria-hidden="true"
										width={20}
										height={20}
									/>
								}
								sx={{
									fontFamily: "canada-type-gibson, sans-serif",
									fontWeight: "var(--font-weight-medium)",
									fontSize: 13
								}}
							>
								{ReturnAuthorizationFormText.toUpperCase()}
							</Button>
						)}
					</OrderDetailsLinksContainer>
					<Drawer
						anchor="right"
						open={isOpen}
						onClose={handleOnClose}
					>
						<DrawerContainer>
							<FlyoutHeader handleOnClose={handleOnClose} />
							{linkedOrdersCount > 0 && (
								<>
									<Divider />
									<NumberOfOrders data-testid="number-of-linkedOrders">
										{linkedOrdersCount} {OrdersText}
									</NumberOfOrders>
								</>
							)}
							{error && (
								<EmptyErrorStates
									helperText={ErrorLinkedOrdersHelperText}
									image="/assets/UnauthorizedSadImage.svg"
									titleText={LinkedOrdersErrorHeaderText}
								/>
							)}

							{!error && linkedOrdersCount === 0 && (
								<EmptyErrorStates
									helperText={EmptyLinkedOrdersHelperText}
									image="/assets/NoOrdersSadBoxImage.svg"
									titleText={NoLinkedOrdersHeaderText}
								/>
							)}

							{linkedOrdersCount > 0 && (
								<ShoppingCartFlyoutScrollableWrapper isDetailsPage>
									{linkedOrders?.map(
										(associatedOrder) =>
											associatedOrder && (
												<IndividualLinkedOrderCard
													key={associatedOrder.orderId}
													associatedOrder={associatedOrder}
													goToOrderDetails={goToOrderDetails}
												/>
											)
									)}
								</ShoppingCartFlyoutScrollableWrapper>
							)}
						</DrawerContainer>
					</Drawer>
				</OrderDetailsHeaderContentDetails>
				<ButtonContainer>
					<SupportCTA
						fullWidth
						href={supportFormUrl}
						className="order-details-action-button"
						data-testid="order-details-support"
						data-id="order-details-support-button"
						aria-label="support"
					/>
					{!isMerchOrder && isReplacementEligible && (
						<Button
							fullWidth
							variant="outlined"
							className="order-details-action-button"
							data-testid="order-replacements"
							data-id="order-replacements-button"
							onClick={() => handleOrderReplacements()}
							aria-label="order replacements"
						>
							{OrderReplacementsText}
						</Button>
					)}
					{!isMerchOrder &&
						configurationsData?.featureFlags?.createOrdersEnabled &&
						orderDetail.orderType !== OrderType.CANCELLED &&
						orderDetail.isAddOnEligible && (
							<Button
								fullWidth
								variant="outlined"
								className="order-details-action-button"
								data-testid="order-add-ons"
								onClick={goToAddOnOrder}
								data-id="order-add-ons-button"
								aria-label="order add ons"
							>
								{isLowesUser ? LowesOrderAddOns : OrderAddOns}
							</Button>
						)}
					{!isMerchOrder &&
						orderDetail.isCancelEligible &&
						orderDetail.cancellationStatus !== OrderActionType.CancellationRequested && (
							<Button
								fullWidth
								variant="outlined"
								className="order-details-action-button"
								data-testid="cancel-order"
								onClick={handleCancelOrder}
								data-id="cancel-order-button"
								aria-label="cancel order"
							>
								<CancelIcon sx={{ height: "18px" }} /> {CancelOrderText}
							</Button>
						)}
					<ExportButtons
						className="order-details-action-button order-details-export-button"
						largePDF={false}
						generatePDF={generateOrderDetailsPDF}
						buttonHeader={isAcknowledgements ? exportAcknowledgementText : exportOrderDetailsText}
						generateExcel={async () =>
							await generateMyOrderDetailsExcel(
								orderDetail,
								Boolean(isAcknowledgements),
								isMerchOrder ? undefined : effectiveAckPermission
							)
						}
						isAcknowledgements={Boolean(isAcknowledgements)}
						isOrderDetailsStyled
						isMerchOrder={isMerchOrder}
					/>
				</ButtonContainer>
			</OrderDetailsHeaderContent>

			{orderDetail.orderType !== OrderType.BILLING_ONLY && (
				<OrderDetailsGrayBox>
					<StatusWrapper>
						<StatusContainer>
							<OrderNumberPoNumberWrapper data-testid="orderDetailsHeader-PONumber">
								<OrderNumberPoNumberText>{PONumberText}</OrderNumberPoNumberText>
								{orderDetail.poNumber}
							</OrderNumberPoNumberWrapper>
							<OrderNumberPoNumberDivider>
								<Divider orientation="vertical" />
							</OrderNumberPoNumberDivider>
							<OrderNumberPoNumberWrapper data-testid="orderDetailsHeader-confirmationNumber">
								<OrderNumberPoNumberText>{ConfirmationNumberText}</OrderNumberPoNumberText>
								{checkForAndReplaceNullEmptyWhitespace(orderDetail.confirmationNumber)}
							</OrderNumberPoNumberWrapper>
							<OrderNumberPoNumberDivider>
								<Divider orientation="vertical" />
							</OrderNumberPoNumberDivider>
							<OrderNumberPoNumberWrapper data-testid="orderDetails-orderNumberColumn">
								<OrderNumberPoNumberText>{OrderNumberText}</OrderNumberPoNumberText>
								{orderDetail.orderNumber}
							</OrderNumberPoNumberWrapper>
						</StatusContainer>

						<span
							className="overline"
							data-testid="orderDetailsHeader-statusUpdatedDate"
						>
							{StatusUpdatedText}
							{orderDetailsHeaderStatusUpdate !== null
								? dateFormat(String(orderDetailsHeaderStatusUpdate))
								: UnavailableDataPlaceholderText}
						</span>
					</StatusWrapper>

					<Divider />

					<PillContainer>
						{orderDetail.status && (
							<OrderSummaryCardChip
								label={convertToTitleCase(orderDetail.status)}
								chipTitle={orderDetail.status}
								data-testid="orderDetailsHeader-status"
							/>
						)}

						<StatusReason
							statusReasonColor={defaultColor}
							data-testid="orderDetailsHeader-statusReasonMessage"
						>
							{statusReasonMessage}
						</StatusReason>
					</PillContainer>

					{orderDetail.status !== OrderStatus.CANCELLED && (
						<DeliveryContent>
							<div>
								{showEstimatedDeliveryText(orderDetail?.status) && (
									<EstimatedDelivery
										className="subtitle1"
										data-testid="orderDetailsHeader-estimatedDelivery"
									>
										{EstimatedDeliveryText}
										<Tooltip
											data-testid="orderDetailsHeader-estimatedDelivery-toolTip"
											title={EstimatedDeliveryToolTipDesc}
											placement="top"
										>
											<EstimatedDeliveryToolTip>
												<InfoOutlined data-testid="estimated-delivery-tooltip-icon" />
											</EstimatedDeliveryToolTip>
										</Tooltip>
									</EstimatedDelivery>
								)}
								{orderDetail.eta && (
									<OrderETA data-testid="orderDetailsHeader-eta">{orderDetail.eta}</OrderETA>
								)}
								{orderDetail.requestedDeliveryDate && (
									<RequestedDateContainer>
										{NewOrderRequestedDeliveryDate}
										<div data-testid="orderDetails-requestedDeliveryDateColumn">
											{dateFormat(orderDetail.requestedDeliveryDate)}
										</div>
									</RequestedDateContainer>
								)}
							</div>

							<CarrierInfoContainer>
								{carrierInfo.map(
									({ info, dataTestId, label }) =>
										((typeof info === "string" && info) ||
											(typeof info === "object" &&
												"trackingNumber" in (info ?? {}) &&
												info?.trackingNumber)) && (
											<div
												className="body2"
												data-testid={dataTestId}
												key={label}
											>
												<b className="subtitle2">{label}</b>
												{renderTrackingUrls(info)}
											</div>
										)
								)}
							</CarrierInfoContainer>
						</DeliveryContent>
					)}
				</OrderDetailsGrayBox>
			)}

			{orderDetail.cancellationStatus === CancellationStatus.CancellationRequested && (
				<Alert
					severity="info"
					sx={{ marginTop: "16px" }}
					data-testid="cancellation-requested-banner"
				>
					<AlertTitle>{CancellationSubmittedBannerTitle}</AlertTitle>
					{CancellationSubmittedBannerDetails}
				</Alert>
			)}

			{orderDetail.cancellationStatus === CancellationStatus.CancellationDenied && (
				<Alert
					severity="error"
					sx={{ marginTop: "16px" }}
					data-testid="cancellation-denied-banner"
				>
					<AlertTitle>{CancellationNotProcessedBannerTitle}</AlertTitle>
					{CancellationNotProcessedBannerDetails}
				</Alert>
			)}

			{hasReplacementOrder && (
				<ReplacementOrderSubmittedBanner data-testid="orderDetailsHeader-replacement-submitted-banner">
					<InfoOutlined />
					<div>
						<p>{ReplacementOrderSubmitted}</p>
						<p>{ReplacementOrderSubmittedText}</p>
					</div>
				</ReplacementOrderSubmittedBanner>
			)}
		</div>
	);
};

interface Props {
	orderDetail: models["OrderDetailViewModel"];
	groupedOrders: models["OrderDetailViewModel"][];
}

export default OrderDetailsHeader;
